import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { history } from '../..';
import { useTranslation } from 'react-i18next';
import ReactShadowRoot from 'react-shadow-root';

import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Tooltip from '@material-ui/core/Tooltip';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import DoneIcon from '@material-ui/icons/Done';
import ClearIcon from '@material-ui/icons/Clear';
import DescriptionIcon from '@material-ui/icons/DescriptionOutlined';

import pushToGTM, { CLICK_DATASHEET_GA4_TAG, clickSelectDesignGA4Tag } from '../../utils/analytics/gtm';
import { tabRoutes } from '../../routes';
import { URL } from '../../utils/constants';
import selectedDesignSlice from '../../redux/slices/selectedDesignSlice';
import tabbarSlice from '../Tabbar/tabbarSlice';
import selectTableSlice from './selectTableSlice';
import { useStyles } from './styles';
import { getSorting, stableSort } from '../Table/utils';
import SelectTableFooter from './SelectTableFooter';
import TableHead from '../Table/TableHead';
import Paper from '../Paper';
import Schematic from '../Schematic';
import Link from '../Link';
import Button from '../Button';
import { logEventType, middlePartOfRedirectLink } from '../../utils/analytics/adobe/adob';
import queryString from 'query-string';
import { getDomain } from '../../utils/config';
import { localLogOpenDesign, localView } from '../../utils/analytics/local';

const SelectTable = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const localDomain = getDomain();
	const localDomainSupport = `https://www.${localDomain}/support`;
	const [isLoading, setIsLoading] = useState(true);
	const items = useSelector(state => state.designList.items);
	const filteredItems = useSelector(state => state.designList.filteredItems);
	const columns = useSelector(state => state.selectTable.columns);
	const order = useSelector(state => state.selectTable.order);
	const orderBy = useSelector(state => state.selectTable.orderBy);
	const actPage = useSelector(state => state.selectTable.actPage);
	const itemsPerPage = useSelector(state => state.selectTable.itemsPerPage);
	const inputParametersDL = useSelector(state => state.designList.inputParameters);
	const dispatch = useDispatch();
	const domain = window.location.origin;
	const fullURL = window.location.href;
	const path = window.location.pathname;

	const datasheetTrackingParameters = item => {
		return {
			redirectLink: item.datasheetUrl === '' ? URL.ONSEMI : item.datasheetUrl,
			partNumber: item.partNumber,
			inputParameters: JSON.stringify(inputParametersDL),
			buttonName: 'Datasheet',
			wdtTabName: 'select',
			fullURL: fullURL,
			path: path,
			logEventType: logEventType.DATASHEETLOG
		};
	};

	useEffect(() => {
		setIsLoading(true);

		const timer = setTimeout(() => {
			setIsLoading(false);
		}, 400);

		return () => {
			clearTimeout(timer);
		};
	}, [filteredItems.length]);

	const setPage = page => {
		dispatch(selectTableSlice.actions.setPage(page));

		if (page === Math.ceil(filteredItems.length / itemsPerPage) - 1) {
			window.scroll(0, 0);
		}
	};

	const setIcPartNumber = icPartNumber => {
		dispatch(selectedDesignSlice.actions.setIcPartNumber(icPartNumber));
	};

	const handleRequestSort = (_event, property) => {
		const isDesc = orderBy === property && order === 'desc';
		dispatch(
			selectTableSlice.actions.changeOrder({
				order: isDesc ? 'asc' : 'desc',
				orderBy: property
			})
		);
		setPage(0);
	};

	const handleSelectDesignClick = (icPartNumber, topology) => {
		pushToGTM(clickSelectDesignGA4Tag(icPartNumber));
		localLogOpenDesign(icPartNumber);

		setIcPartNumber(icPartNumber);
		dispatch(selectedDesignSlice.actions.setTopology(topology));
		dispatch(tabbarSlice.actions.enableDesignTabs());
		dispatch(selectedDesignSlice.actions.setFetchDesignData(true));

		history.push(tabRoutes.find(route => route.path === '/summary').path);
	};

	const truncate = (str, n) => (str.length > n ? str.substr(0, n - 1) + '...' : str);

	return (
		<Paper
			padding={[0, 2]}
			style={{
				borderBottom: filteredItems.length <= itemsPerPage && '0 !important'
			}}>
			<div className={classes.tableWrapper}>
				<Backdrop className={classes.backdrop} open={filteredItems.length > 0 ? isLoading : false}>
					<CircularProgress color="inherit" />
				</Backdrop>

				<TableContainer>
					<Table id="Select__table" className={classes.table} aria-labelledby="tableTitle">
						<TableHead
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
							columns={columns}
							withActionCell
						/>

						<TableBody>
							{items.length < 1 || filteredItems.length < 1 ? (
								<TableRow>
									<TableCell colspan={columns.length} className={classes.noDesignsFoundTableCell}>
										<Grid
											container
											direction="column"
											alignItems="center"
											spacing={3}
											className={classes.noDesignsFoundContainer}>
											<Grid item>
												<Typography variant="body1">
													{items.length < 1 ? (
														<>
															{t('errors.noDesignsFound')}
															<Link href={localDomainSupport} target="_blank">
																{`https://www.${localDomain}/support`}
															</Link>
															.
														</>
													) : (
														t('errors.noDesignsFilter')
													)}
												</Typography>
											</Grid>

											{items.length < 1 && (
												<Grid item>
													<Button
														color="primary"
														variant="contained"
														onClick={() => {
															history.replace(tabRoutes.find(route => route.path === '/inputs').path);
														}}>
														{t('components.select.newDesign')}
													</Button>
												</Grid>
											)}
										</Grid>
									</TableCell>
								</TableRow>
							) : (
								stableSort(filteredItems, getSorting(order, orderBy))
									.slice(actPage * itemsPerPage, actPage * itemsPerPage + itemsPerPage)
									.map((item, index) => (
										<TableRow role="checkbox" tabIndex={-1} key={'select-table-row-' + item.partNumber + index}>
											{columns.map((col, index2) => (
												<TableCell
													key={'select-table-cell-' + col.key + index2}
													align="left"
													className={col.key === 'schematic' && classes.schematicCell}
													style={col.key === 'selectButton' ? { paddingLeft: '0' } : undefined}>
													{col.key === 'schematic' ? (
														<div onClick={() => handleSelectDesignClick(item.partNumber, item.topology)}>
															<ReactShadowRoot>
																<Schematic
																	topology={item.topology}
																	icPartNumber={item.partNumber}
																	topologyCategory={item.topologyCategory}
																	schematic={item.schematic}
																	page={'SELECT'}
																	itemId={item.icID}
																/>
															</ReactShadowRoot>
														</div>
													) : col.key === 'selectButton' ? (
														<div className={`${classes.selectButtonCell} ${classes.centerAlign}`}>
															<Typography variant="body1">{truncate(item.partNumber, 25)}</Typography>
															<Button
																color="primary"
																variant="contained"
																className={`${classes.selectButton} ${classes.focusableButton}`}
																onClick={() => {
																	localView('Design schematic', item.partNumber);
																	handleSelectDesignClick(item.partNumber, item.topology);
																}}>
																<Typography>{t('components.select.viewDesign')}</Typography>
															</Button>
															<br />
															<div className={`${classes.linkContainer} ${classes.centerAlign}`}>
																<Tooltip title={t('components.general.viewDatasheet')}>
																	<Link
																		className={classes.linkStyle}
																		href={
																			domain +
																			middlePartOfRedirectLink +
																			queryString.stringify(datasheetTrackingParameters(item))
																		}
																		target="_blank"
																		onClick={() => {
																			pushToGTM(CLICK_DATASHEET_GA4_TAG);
																		}}>
																		<DescriptionIcon fontSize="small" /> {t('components.design.openDatasheet')}
																	</Link>
																</Tooltip>
															</div>
														</div>
													) : typeof item[col.key] === 'boolean' ? (
														item[col.key] === true ? (
															<DoneIcon fontSize="small" />
														) : (
															<ClearIcon fontSize="small" />
														)
													) : item[col.key] === null ? (
														<Typography>{`NA`}</Typography>
													) : (
														<Typography>{t(`topologies:${item[col.key]}`)}</Typography>
													)}
												</TableCell>
											))}
										</TableRow>
									))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</div>
			<SelectTableFooter
				numberOfFilteredItems={filteredItems.length}
				itemsPerPage={itemsPerPage}
				actPage={actPage}
				setPage={setPage}
			/>
		</Paper>
	);
};

export default SelectTable;
